// Chip (tag) input for multi-value criteria — the IN-list entry used by the
// visual/dashboard criteria form (runtime) and the designers (compare value /
// default value). Values live as a string[] in the caller; the canonical wire
// format everywhere else is the comma-joined string the BI engine already
// splits (`_resolve_effective_params`), so hosts typically join(',') on change.
//
// Interactions: type + Enter (or comma) adds a chip, Backspace on an empty
// input removes the last chip, each chip has an ✕. Date/number types render
// the matching input so the picker/keypad appears.
//
// The comma is committed from KEYDOWN, not from the typed value: a
// `type="number"` input silently refuses the character (and rejects a pasted
// comma list wholesale), so a numeric IN-list could only be built one chip at a
// time with Enter or by clicking away. Handling the key — and the paste —
// directly makes comma entry work for every input type.

import { useRef, useState } from 'react'
import { X } from 'lucide-react'

export interface ChipInputProps {
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  disabled?: boolean
  /** HTML input type for the entry field — 'text' | 'number' | 'date'. */
  inputType?: string
  style?: React.CSSProperties
  /** Extra keydown hook on the entry input (e.g. F2 to open a lookup). */
  onEntryKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  title?: string
}

export default function ChipInput({
  values, onChange, placeholder, disabled, inputType = 'text', style,
  onEntryKeyDown, title,
}: ChipInputProps) {
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const commit = (raw?: string) => {
    const text = (raw ?? draft).trim()
    if (!text) return
    // Pasted comma lists split into individual chips; duplicates are dropped.
    const parts = text.split(',').map((s) => s.trim()).filter(Boolean)
    const next = [...values]
    for (const p of parts) if (!next.includes(p)) next.push(p)
    if (next.length !== values.length) onChange(next)
    setDraft('')
  }

  const removeAt = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx))
  }

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      title={title}
      style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4,
        padding: '4px 6px', minHeight: 32, boxSizing: 'border-box',
        border: '1px solid var(--color-border)', borderRadius: 6,
        background: disabled
          ? 'var(--color-surface-2, var(--color-bg-secondary))'
          : 'var(--color-input-bg, var(--color-bg))',
        cursor: disabled ? 'default' : 'text',
        ...style,
      }}
    >
      {values.map((v, i) => (
        <span
          key={`${v}-${i}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '1px 4px 1px 8px', borderRadius: 999,
            fontSize: 13, lineHeight: '18px', maxWidth: '100%',
            background: 'var(--color-accent-weak, rgba(76,141,255,0.15))',
            border: '1px solid color-mix(in srgb, var(--color-accent, #4c8dff) 40%, transparent)',
            color: 'var(--color-text)',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {v}
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeAt(i) }}
              title={`Remove ${v}`}
              aria-label={`Remove ${v}`}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 16, height: 16, padding: 0, border: 'none', borderRadius: '50%',
                background: 'transparent', cursor: 'pointer',
                color: 'var(--color-text-secondary)',
              }}
            >
              <X size={11} />
            </button>
          )}
        </span>
      ))}
      <input
        ref={inputRef}
        type={inputType}
        value={draft}
        disabled={disabled}
        placeholder={values.length === 0 ? placeholder : undefined}
        onChange={(e) => {
          const v = e.target.value
          // A date input "commits" the moment a full date is picked.
          if (inputType === 'date' && v) { commit(v); return }
          // Typing/pasting a comma commits the finished value(s).
          if (v.includes(',')) { commit(v); return }
          setDraft(v)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && draft.trim()) {
            e.preventDefault()
            e.stopPropagation() // don't trigger the host's run-on-Enter
            commit()
            return
          }
          // Comma = "chip finished". Always preventDefault so the separator
          // never lands in the field (a number input drops it anyway, a text
          // one would otherwise keep it and commit twice via onChange).
          if (e.key === ',') {
            e.preventDefault()
            if (draft.trim()) commit()
            return
          }
          if (e.key === 'Backspace' && !draft && values.length > 0) {
            removeAt(values.length - 1)
            return
          }
          onEntryKeyDown?.(e)
        }}
        onPaste={(e) => {
          // A number/date input rejects a pasted comma list outright — the
          // value never reaches onChange — so split it here for every type.
          const text = e.clipboardData.getData('text')
          if (!text.includes(',')) return
          e.preventDefault()
          commit(`${draft}${text}`)
        }}
        onBlur={() => commit()}
        style={{
          flex: 1, minWidth: inputType === 'date' ? 130 : 70, border: 'none',
          outline: 'none', background: 'transparent', padding: '2px 2px',
          fontSize: 14, color: 'var(--color-text)', fontFamily: 'inherit',
        }}
      />
    </div>
  )
}
